import { Check, ChevronDown, Search, X, type LucideIcon } from 'lucide-react'
import {
  memo,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactNode,
} from 'react'

import { COMMON } from '@brickup/shared/locales/de/common'
import { CONTROLS } from '@brickup/shared/locales/de/controls'
import { Badge } from './Badge'
import { cn } from './cn'
import { useDisabled, useQuietField } from './control-scope'
import { controlShell, Field } from './Field'
import { Popover } from './Popover'
import { FOCUS_RING, MENU_ITEM_CLASS, MENU_SEPARATOR_CLASS } from './recipes'
import { rowActivation } from './Table'
import type { Tone } from './tone'

/** Above this many options the list carries a search field. */
const SEARCH_THRESHOLD = 5

/**
 * Rows the list renders at once. A caller filtering client-side passes a short
 * list; the cap is what stops one that passes ten thousand from blocking the
 * main thread on a single open.
 */
const MAX_RENDERED_OPTIONS = 200

/** How long after one letter the next still extends the same type-ahead. */
const TYPE_AHEAD_MS = 1000

export type SelectOption = {
  value: string
  label: string
  disabled?: boolean
  /** Drawn before the label, in the list and in the trigger. */
  icon?: LucideIcon
  /** Tints the icon and the label — a status colour, not decoration. */
  className?: string
  /** A pill at the row's end: which kind of thing the option is, where the
   *  list mixes kinds. */
  badge?: { label: string; variant?: Tone }
  /** More text the search matches beside the label, never drawn: what a
   *  caller-drawn row shows that the label does not. */
  searchText?: string
  /** The row as the caller draws it, in the list only, in place of the
   *  label and the badge; the trigger and the search still read the label
   *  and the search text. */
  content?: ReactNode
}

type SelectProps = {
  value: string | null | undefined
  onChange: (value: string) => void
  options: SelectOption[]
  label?: string
  helper?: string
  error?: string
  placeholder?: string
  /** The search field's placeholder, once the list is long enough to have one. */
  searchPlaceholder?: string
  disabled?: boolean
  className?: string
  /** Datasheet row: no box until hover or focus. */
  quiet?: boolean
  /** Replaces the bordered trigger — for a toolbar chip that carries no box. */
  trigger?: (props: {
    selected: SelectOption | undefined
    placeholder: string | undefined
  }) => ReactNode
  /**
   * Takes over filtering. The caller updates `options` as the text changes,
   * for an option set too large to ship to the browser. Implies the search
   * field whatever the current number of options.
   */
  onSearchChange?: (search: string) => void
  /**
   * Empties the pick. Passing it puts the cross on the trigger beside the
   * chevron, for a choice that is genuinely optional — a list offers no way
   * back to nothing, and every picker without this one could be set and never
   * unset.
   *
   * Its own handler, not a null through `onChange`: a caller that cannot be
   * emptied should not have to handle a value it never receives.
   */
  onClear?: () => void
  /**
   * One thing to do under the list that is not a pick — adding what the
   * list does not have yet. The last row of the list, reached by the keys
   * like any other; closes the list as it is chosen.
   */
  footer?: SelectFooter
}

export type SelectFooter = {
  label: string
  icon?: LucideIcon
  onSelect: () => void
  /** The list stays open: the footer changes what it offers, such as
   *  widening it to every option. */
  keepsOpen?: boolean
}

/** What the highlight holds while it rests on the footer. */
const FOOTER_KEY = Symbol('footer')

/**
 * A single choice from a list.
 *
 * Past `SEARCH_THRESHOLD` options the list opens with a search field above
 * it; shorter, it is the bare list. One popover either way, so the trigger,
 * the keyboard and the portal behave the same whatever the length.
 */
export function Select({
  value,
  onChange,
  options,
  label,
  helper,
  error,
  placeholder,
  searchPlaceholder = CONTROLS.searchPlaceholder,
  disabled: disabledProp,
  className,
  quiet: quietProp,
  trigger,
  onSearchChange,
  onClear,
  footer,
}: SelectProps) {
  const [open, setOpen] = useState(false)
  // Whether the press that opened the list was a finger. The list reads it as
  // it mounts: a focused search field would raise the on-screen keyboard over
  // it on every open.
  const openedByTouch = useRef(false)
  const quiet = useQuietField(quietProp)
  const disabled = useDisabled(disabledProp)

  const selected = options.find(option => option.value === value)
  const searchable = !!onSearchChange || options.length > SEARCH_THRESHOLD

  // The list's own search resets as it unmounts; a server-side one is the
  // caller's state and is told here, on every way the list closes.
  const close = () => {
    setOpen(false)
    onSearchChange?.('')
  }

  // Picking what is already picked changes nothing, so the caller hears
  // nothing: a select that saves on change would send the same value again.
  const pick = (option: SelectOption) => {
    if (option.value !== value) onChange(option.value)
    close()
  }
  const pickFooter = () => {
    if (!footer?.keepsOpen) close()
    footer?.onSelect()
  }

  // A select disabled while its list is open closes it, so no pick reaches a
  // caller that has just refused one.
  useEffect(() => {
    if (disabled && open) close()
  }, [disabled, open])

  return (
    <Field label={label} helper={helper} error={error} className={className}>
      {field => (
        <Popover
          open={open}
          onOpenChange={next => (next ? setOpen(true) : close())}
          // The list focuses its own target as it mounts.
          onOpenAutoFocus={event => event.preventDefault()}
          className={cn(
            'overflow-hidden p-0',
            // A searched list keeps one width while the matches change under
            // the field; a bare one is as wide as its longest option.
            searchable
              ? 'w-[var(--radix-popover-trigger-width)] min-w-56'
              : 'w-max max-w-[var(--radix-popover-content-available-width)] min-w-[var(--radix-popover-trigger-width)]',
          )}
          trigger={
            <button
              type="button"
              {...field}
              disabled={disabled}
              onPointerDown={event => {
                openedByTouch.current = event.pointerType === 'touch'
              }}
              onKeyDown={event => {
                openedByTouch.current = false
                if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return
                event.preventDefault()
                setOpen(true)
              }}
              // A caller's trigger is what the button wears; the ring stays
              // the button's, since only the button takes focus.
              className={
                trigger
                  ? cn('rounded-md outline-none', FOCUS_RING)
                  : controlShell(quiet, TRIGGER_LAYOUT)
              }
            >
              {trigger ? (
                trigger({ selected, placeholder })
              ) : (
                <TriggerFace
                  selected={selected}
                  // A value whose option has not loaded yet reads blank, not
                  // as the placeholder an unset field shows.
                  text={selected ? selected.label : value ? null : placeholder}
                  onClear={disabled ? undefined : onClear}
                />
              )}
            </button>
          }
        >
          <OptionList
            value={value}
            options={options}
            searchable={searchable}
            touch={openedByTouch.current}
            searchPlaceholder={searchPlaceholder}
            onSearchChange={onSearchChange}
            footer={footer}
            marked={selected !== undefined}
            onPick={pick}
            onPickFooter={pickFooter}
          />
        </Popover>
      )}
    </Field>
  )
}

const TRIGGER_LAYOUT = cn(
  'flex flex-row items-center justify-between gap-2',
  // A long option truncates rather than wrapping: a trigger that grows to two
  // lines drags the row it sits in out of step with every other row in the
  // form.
  'overflow-hidden whitespace-nowrap',
)

/** The bordered trigger's contents. */
function TriggerFace({
  selected,
  text,
  onClear,
}: {
  selected: SelectOption | undefined
  text: string | null | undefined
  onClear?: () => void
}) {
  return (
    <>
      <span
        className={cn(
          'flex min-w-0 flex-row items-center gap-1.5',
          selected ? selected.className : 'text-faint',
        )}
      >
        {selected?.icon ? (
          <selected.icon size={14} className="shrink-0" />
        ) : null}
        <span className="min-w-0 truncate">{text}</span>
      </span>
      <span className="flex shrink-0 flex-row items-center gap-1">
        {/* A span, not a button: the trigger is one, and a button inside a
            button is not a thing a browser will render. The press is stopped
            before it opens the list. */}
        {onClear && selected ? (
          <span
            role="button"
            aria-label={COMMON.clearSelection}
            onPointerDown={event => {
              event.preventDefault()
              event.stopPropagation()
            }}
            {...rowActivation(event => {
              event.preventDefault()
              event.stopPropagation()
              onClear()
            })}
            className={cn(
              'rounded-sm text-faint hover:text-foreground-2',
              FOCUS_RING,
            )}
          >
            <X size={14} />
          </span>
        ) : null}
        <ChevronDown size={14} className="text-faint" />
      </span>
    </>
  )
}

/**
 * The open list. Mounted on every open and gone on close, so the search and
 * the highlight start fresh each time.
 */
function OptionList({
  value,
  options,
  searchable,
  touch,
  searchPlaceholder,
  onSearchChange,
  footer,
  marked,
  onPick,
  onPickFooter,
}: {
  value: string | null | undefined
  options: SelectOption[]
  searchable: boolean
  /** Opened by a finger: the list takes the focus, not the search field. */
  touch: boolean
  searchPlaceholder: string
  onSearchChange?: (search: string) => void
  footer?: SelectFooter
  /** Whether some option is picked, so every row keeps the slot its check
   *  would take; without a pick the slot is a gutter of nothing. */
  marked: boolean
  onPick: (option: SelectOption) => void
  onPickFooter: () => void
}) {
  const [search, setSearch] = useState('')
  // The highlighted option by its value, so a list that reloads or reorders
  // while open keeps the highlight on the same option; the footer by its
  // own mark, since it has no value.
  const [activeValue, setActiveValue] = useState<
    string | typeof FOOTER_KEY | null
  >(value ?? null)
  const boxRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  const { shown, total } = useMemo(() => {
    const needle = search.toLowerCase()
    const matches =
      onSearchChange || !needle
        ? options
        : options.filter(option =>
            `${option.label} ${option.searchText ?? ''}`
              .toLowerCase()
              .includes(needle),
          )
    return {
      shown: matches.slice(0, MAX_RENDERED_OPTIONS),
      total: matches.length,
    }
  }, [options, search, onSearchChange])
  // The footer is one row past the options, whatever the search says:
  // reached by the arrow keys, never matched, never picked.
  const FOOTER = footer ? shown.length : -1
  const rows = shown.length + (footer ? 1 : 0)

  /** The nearest enabled row from `from` in `by`, or -1 past either end. */
  const step = (from: number, by: 1 | -1) => {
    for (let i = from + by; i >= 0 && i < rows; i += by) {
      if (i === FOOTER || !shown[i].disabled) return i
    }
    return -1
  }

  const highlighted =
    activeValue === FOOTER_KEY
      ? FOOTER
      : shown.findIndex(
          option => option.value === activeValue && !option.disabled,
        )
  // Without a highlight of its own the list rests on its first enabled row,
  // which is where a fresh search or a reload that dropped the row lands.
  const active = highlighted === -1 ? step(-1, 1) : highlighted

  /**
   * Scrolls the list just far enough to show the row. Set on the list's own
   * box: `scrollIntoView` would also move the page, and on open the popover
   * has not yet been placed where it will sit.
   */
  const reveal = (index: number) => {
    const box = boxRef.current
    const row = box?.querySelector<HTMLElement>(`[data-index="${index}"]`)
    if (!box || !row) return
    const top = row.offsetTop
    const bottom = top + row.offsetHeight
    if (top < box.scrollTop) box.scrollTop = top
    else if (bottom > box.scrollTop + box.clientHeight) {
      box.scrollTop = bottom - box.clientHeight
    }
  }

  // On open, and again when options loading into an open list bring the
  // search field: the keys go to the field, or to the list where there is
  // none or a finger opened it, and the highlighted row comes into view.
  useLayoutEffect(() => {
    const target = (touch ? null : inputRef.current) ?? boxRef.current
    target?.focus({ preventScroll: true })
    reveal(active)
  }, [searchable])

  const highlight = (index: number) => {
    if (index === -1) return
    setActiveValue(index === FOOTER ? FOOTER_KEY : shown[index].value)
    reveal(index)
  }
  const choose = (index: number) => {
    if (index === FOOTER) onPickFooter()
    else if (index !== -1) onPick(shown[index])
  }

  /** The first enabled option whose label starts with `prefix`, counting
   *  from the highlight (`first` 0) or the row after it (1), wrapping round;
   *  -1 if none. */
  const nextStartingWith = (prefix: string, first: 0 | 1) => {
    for (let offset = first; offset < shown.length + first; offset++) {
      const index = (active + offset + shown.length) % shown.length
      const option = shown[index]
      if (!option.disabled && option.label.toLowerCase().startsWith(prefix)) {
        return index
      }
    }
    return -1
  }

  // What has been typed into the bare list, and when, so letters typed in
  // quick succession spell one prefix.
  const typed = useRef({ text: '', at: 0 })

  /** Type-ahead: letters typed together find the option they begin; one
   *  letter pressed again cycles through the options starting with it. */
  const typeAhead = (letter: string) => {
    const now = Date.now()
    const text =
      (now - typed.current.at < TYPE_AHEAD_MS ? typed.current.text : '') +
      letter
    typed.current = { text, at: now }
    const cycling = [...text].every(char => char === letter)
    return cycling ? nextStartingWith(letter, 1) : nextStartingWith(text, 0)
  }

  const onKeyDown = (event: KeyboardEvent) => {
    const { key } = event
    const inField = event.target === inputRef.current
    const printable =
      key.length === 1 && !event.ctrlKey && !event.metaKey && !event.altKey

    if (key === 'ArrowDown') highlight(step(active, 1))
    else if (key === 'ArrowUp') highlight(step(active, -1))
    else if (key === 'Enter' || (key === ' ' && !inField)) choose(active)
    // Every other key edits the text while the search field has the focus.
    else if (inField) return
    else if (key === 'Home') highlight(step(-1, 1))
    else if (key === 'End') highlight(step(rows, -1))
    else if (printable) highlight(typeAhead(key.toLowerCase()))
    else return
    event.preventDefault()
  }

  return (
    <div onKeyDown={onKeyDown}>
      {searchable ? (
        // A label, so the icon and the padding beside the field focus it too
        // — the whole row is the target.
        <label className="flex cursor-text flex-row items-center gap-2 border-b border-line-soft px-3 py-2">
          <Search size={14} className="text-faint" aria-hidden />
          <input
            ref={inputRef}
            type="text"
            value={search}
            placeholder={searchPlaceholder}
            onChange={event => {
              setSearch(event.target.value)
              setActiveValue(null)
              onSearchChange?.(event.target.value)
            }}
            className="min-w-0 flex-1 bg-transparent text-[length:var(--control-text)] outline-none"
          />
        </label>
      ) : null}

      {/* Positioned, so a row's `offsetTop` is measured from this box. Takes
          the focus itself where there is no search field. */}
      <div
        ref={boxRef}
        tabIndex={-1}
        className="relative max-h-72 overflow-y-auto p-1 outline-none"
      >
        {total === 0 ? (
          <p className="px-2 py-3 text-sm text-muted-foreground">
            {CONTROLS.noResults}
          </p>
        ) : null}
        {shown.map((option, index) => (
          <OptionRow
            key={option.value}
            option={option}
            index={index}
            active={index === active}
            picked={option.value === value}
            marked={marked}
            onPick={onPick}
            onHover={setActiveValue}
          />
        ))}
        {total > shown.length ? (
          <p className="px-2 py-2 text-xs text-faint">
            {CONTROLS.select.truncated(shown.length, total)}
          </p>
        ) : null}
        {footer ? (
          <>
            <div className={MENU_SEPARATOR_CLASS} />
            <OptionRow
              option={{ value: '', label: footer.label, icon: footer.icon }}
              index={FOOTER}
              active={active === FOOTER}
              picked={false}
              marked={marked}
              onPick={onPickFooter}
              onHover={() => setActiveValue(FOOTER_KEY)}
            />
          </>
        ) : null}
      </div>
    </div>
  )
}

const OPTION_ROW_CLASS = cn(
  MENU_ITEM_CLASS,
  'w-full text-sm outline-none select-none',
  'disabled:cursor-not-allowed disabled:opacity-40',
)

/** The slot before a row's label where the picked one shows its check.
 *  Only a list with a pick in it carries the slot: without one it is a
 *  gutter of nothing before every row. */
const OPTION_MARK_CLASS = 'w-3.5 shrink-0'

/**
 * One row. Memoised on its own props, so moving the highlight re-renders the
 * two rows it leaves and enters, not the list.
 */
const OptionRow = memo(function OptionRow({
  option,
  index,
  active,
  picked,
  marked,
  onPick,
  onHover,
}: {
  option: SelectOption
  index: number
  active: boolean
  picked: boolean
  /** Whether some row of the list is picked, so every row keeps the slot. */
  marked: boolean
  onPick: (option: SelectOption) => void
  onHover: (value: string) => void
}) {
  return (
    // The focus stays on the search field or the list, so a row is reached by
    // the arrow keys through the highlight and never tabbed to.
    <button
      type="button"
      tabIndex={-1}
      data-index={index}
      disabled={option.disabled}
      // Moved, not entered: a row the keys scroll under a resting pointer
      // would otherwise take the highlight from them.
      onPointerMove={option.disabled ? undefined : () => onHover(option.value)}
      onClick={() => onPick(option)}
      className={cn(OPTION_ROW_CLASS, active && 'bg-line-soft')}
    >
      {marked ? (
        <span className={OPTION_MARK_CLASS}>
          {picked ? <Check size={13} className="text-primary" /> : null}
        </span>
      ) : null}
      {option.icon ? (
        <option.icon size={14} className={cn('shrink-0', option.className)} />
      ) : null}
      {option.content ? (
        option.content
      ) : (
        <span className={cn('min-w-0 truncate', option.className)}>
          {option.label}
        </span>
      )}
      {option.badge && !option.content ? (
        <Badge
          size="sm"
          variant={option.badge.variant}
          label={option.badge.label}
          className="ml-auto"
        />
      ) : null}
    </button>
  )
})
